<template>
  <div class="coupon-code-page">
    <el-form v-if="canCreateCoupon" :inline="!isMobile" :model="createForm" class="create-form">
      <el-form-item label="创建模式">
        <el-radio-group v-model="createMode" size="small">
          <el-radio-button label="batch">批量随机码</el-radio-button>
          <el-radio-button label="custom">自定义码</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="适用赛事">
        <el-select
          v-model="createForm.eventId"
          filterable
          remote
          reserve-keyword
          placeholder="搜索并选择赛事"
          :remote-method="searchEvents"
          :loading="eventLoading"
          no-data-text="请输入关键词搜索赛事"
          class="event-select"
        >
          <el-option
            v-for="event in eventOptions"
            :key="event._id"
            :label="event.name"
            :value="event._id"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="优惠券名称">
        <el-input
          v-model="createForm.name"
          placeholder="例如：早鸟优惠券"
          maxlength="50"
          show-word-limit
          class="coupon-name-input"
        ></el-input>
      </el-form-item>
      <el-form-item label="优惠金额(元)">
        <el-input-number v-model="createForm.discountAmountYuan" :min="0.01" :precision="2"></el-input-number>
      </el-form-item>
      <el-form-item v-if="createMode === 'batch'" label="生成数量">
        <el-input-number v-model="createForm.count" :min="1" :max="500" :step="1"></el-input-number>
      </el-form-item>
      <el-form-item v-if="createMode === 'custom'" label="优惠码">
        <el-input
          v-model="createForm.code"
          placeholder="4-32位字母数字_-"
          maxlength="32"
          class="coupon-code-input"
        ></el-input>
      </el-form-item>
      <el-form-item v-if="createMode === 'custom'" label="总使用次数">
        <el-input-number v-model="createForm.usageLimit" :min="1" :max="10000" :step="1"></el-input-number>
      </el-form-item>
      <el-form-item class="create-action-item">
        <el-button type="primary" icon="el-icon-plus" :loading="creating" @click="createCoupon">
          {{ createMode === 'batch' ? '批量生成' : '创建优惠码' }}
        </el-button>
      </el-form-item>
    </el-form>

    <div class="filter-tools">
      <el-input v-model="filters.eventId" placeholder="按赛事ID筛选" class="filter-input" clearable></el-input>
      <el-input v-model="filters.code" placeholder="按优惠码筛选" class="filter-input" clearable></el-input>
      <el-select v-model="filters.status" placeholder="按状态筛选" clearable class="status-select">
        <el-option label="可用" value="available"></el-option>
        <el-option label="待支付占用" value="locked"></el-option>
        <el-option label="已使用" value="used"></el-option>
        <el-option label="已停用" value="disabled"></el-option>
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
      <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
    </div>

    <el-table ref="couponTable" :data="tableData" style="width: 100%">
      <el-table-column prop="code" label="优惠码" width="140"></el-table-column>
      <el-table-column prop="name" label="优惠券名称" width="160">
        <template slot-scope="scope">
          <span>{{ scope.row.name || '优惠券' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="eventId" label="赛事ID" width="170">
        <template slot-scope="scope">
          <copy-text :value="scope.row.eventId"></copy-text>
        </template>
      </el-table-column>
      <el-table-column prop="eventName" label="赛事名称" width="300">
        <template slot-scope="scope">
          <copy-text :value="scope.row.eventName"></copy-text>
        </template>
      </el-table-column>
      <el-table-column label="比赛时间" width="180">
        <template slot-scope="scope">
          {{ formatDate(scope.row.eventStartTime) }} - {{ formatDate(scope.row.eventEndTime) }}
        </template>
      </el-table-column>
      <el-table-column label="优惠金额" width="100">
        <template slot-scope="scope">{{ divide(scope.row.discountAmount, 100) }} 元</template>
      </el-table-column>
      <el-table-column label="已使用/总次数" width="120">
        <template slot-scope="scope">{{ scope.row.usedCount || 0 }} / {{ scope.row.usageLimit || 1 }}</template>
      </el-table-column>
      <el-table-column label="状态" width="110">
        <template slot-scope="scope">
          <el-tag :type="statusTagType(scope.row.status)" size="mini">{{ statusText(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="待支付占用" width="110">
        <template slot-scope="scope">
          {{ scope.row.lockedCount || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="使用情况" width="260">
        <template slot-scope="scope">
          <div v-if="scope.row.usedOrder">
            <div>运动员：{{ scope.row.usedOrder.name || '-' }}</div>
            <div>优惠前：{{ divide(scope.row.usedOrder.originalPayableAmount, 100) }} 元</div>
            <div>优惠：{{ divide(scope.row.usedOrder.couponDiscountAmount, 100) }} 元</div>
            <div>优惠后：{{ divide(scope.row.usedOrder.payableAmount, 100) }} 元</div>
          </div>
          <span v-else class="empty-text">-</span>
        </template>
      </el-table-column>
      <el-table-column label="使用时间" width="170">
        <template slot-scope="scope">
          <span v-if="scope.row.usedOrder && scope.row.usedOrder.couponUsedAt">{{ formatDateTime(scope.row.usedOrder.couponUsedAt) }}</span>
          <span v-else class="empty-text">-</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170">
        <template slot-scope="scope">{{ formatDateTime(scope.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column v-if="hasOperationActions" label="操作" fixed="right" :width="operationColumnWidth">
        <template slot-scope="scope">
          <div class="coupon-actions">
            <el-button
              v-if="canViewCoupon && scope.row.usedCount > 0"
              type="primary"
              size="mini"
              @click="openUsageDetails(scope.row)"
            >使用详情</el-button>
            <el-button
              v-if="canUpdateCoupon && (scope.row.status === 'locked' || scope.row.lockedCount > 0)"
              type="warning"
              size="mini"
              @click="releaseLock(scope.row)"
            >解除占用</el-button>
            <el-button
              v-if="canUpdateCoupon && scope.row.status !== 'used' && scope.row.status !== 'disabled'"
              type="danger"
              size="mini"
              @click="changeStatus(scope.row, 'disable')"
            >停用</el-button>
            <el-button
              v-if="canUpdateCoupon && scope.row.status === 'disabled'"
              type="success"
              size="mini"
              @click="changeStatus(scope.row, 'enable')"
            >启用</el-button>
            <el-button
              v-if="canDeleteCoupon && (scope.row.usedCount || 0) === 0 && (scope.row.lockedCount || 0) === 0"
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="deleteCoupon(scope.row)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page.sync="pageNum"
      @current-change="onCurrentPageChange"
    ></el-pagination>

    <el-dialog
      :title="`${usageDialog.coupon.code || ''} 使用详情`"
      :visible.sync="usageDialog.visible"
      width="920px"
    >
      <div class="usage-summary">
        {{ usageDialog.coupon.name || '优惠券' }}：
        {{ usageDialog.coupon.usedCount || 0 }} / {{ usageDialog.coupon.usageLimit || 1 }}
      </div>
      <el-table v-loading="usageDialog.loading" :data="usageDialog.list" style="width: 100%">
        <el-table-column prop="name" label="运动员姓名" width="120"></el-table-column>
        <el-table-column prop="eventId" label="赛事ID" width="170">
          <template slot-scope="scope">
            <copy-text :value="scope.row.eventId"></copy-text>
          </template>
        </el-table-column>
        <el-table-column prop="eventName" label="赛事名称" min-width="220">
          <template slot-scope="scope">
            <copy-text :value="scope.row.eventName"></copy-text>
          </template>
        </el-table-column>
        <el-table-column label="比赛时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.eventStartTime) }} - {{ formatDate(scope.row.eventEndTime) }}
          </template>
        </el-table-column>
        <el-table-column label="优惠前" width="90">
          <template slot-scope="scope">{{ divide(scope.row.originalPayableAmount, 100) }} 元</template>
        </el-table-column>
        <el-table-column label="优惠金额" width="90">
          <template slot-scope="scope">{{ divide(scope.row.couponDiscountAmount, 100) }} 元</template>
        </el-table-column>
        <el-table-column label="优惠后" width="90">
          <template slot-scope="scope">{{ divide(scope.row.payableAmount, 100) }} 元</template>
        </el-table-column>
        <el-table-column label="使用时间" width="170">
          <template slot-scope="scope">{{ formatDateTime(scope.row.couponUsedAt) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'loadsh';
import { hasPermission } from '../../utils/auth';

export default {
  data() {
    return {
      createMode: 'batch',
      createForm: {
        eventId: '',
        name: '',
        discountAmountYuan: 100,
        count: 10,
        code: '',
        usageLimit: 1
      },
      filters: {
        eventId: '',
        code: '',
        status: ''
      },
      eventOptions: [],
      eventLoading: false,
      searchEventSeq: 0,
      debouncedSearchEvents: null,
      creating: false,
      tableData: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      usageDialog: {
        visible: false,
        loading: false,
        coupon: {},
        list: []
      },
      isMobile: false
    };
  },
  computed: {
    canViewCoupon() {
      return hasPermission('coupon:view');
    },
    canCreateCoupon() {
      return hasPermission('coupon:create');
    },
    canUpdateCoupon() {
      return hasPermission('coupon:update');
    },
    canDeleteCoupon() {
      return hasPermission('coupon:delete');
    },
    hasOperationActions() {
      return this.canViewCoupon || this.canUpdateCoupon || this.canDeleteCoupon;
    },
    operationColumnWidth() {
      return this.isMobile ? 128 : 280;
    }
  },
  created() {
    this.debouncedSearchEvents = _.debounce(this.requestEvents, 350);
    this.getData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    if (this.debouncedSearchEvents && this.debouncedSearchEvents.cancel) {
      this.debouncedSearchEvents.cancel();
    }
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    divide: _.divide,
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
      this.$nextTick(() => {
        if (this.$refs.couponTable && this.$refs.couponTable.doLayout) {
          this.$refs.couponTable.doLayout();
        }
      });
    },
    formatDate(date) {
      if (!date) return '-';
      return this.$dayjs(date).format('YYYY-MM-DD');
    },
    formatDateTime(date) {
      if (!date) return '-';
      return this.$dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    },
    statusText(status) {
      const map = {
        available: '可用',
        locked: '待支付占用',
        used: '已使用',
        disabled: '已停用'
      };
      return map[status] || '未知';
    },
    statusTagType(status) {
      const map = {
        available: 'success',
        locked: 'warning',
        used: 'info',
        disabled: 'danger'
      };
      return map[status] || 'info';
    },
    searchEvents(keyword) {
      const text = (keyword || '').trim();
      if (!text) {
        this.searchEventSeq++;
        if (this.debouncedSearchEvents && this.debouncedSearchEvents.cancel) {
          this.debouncedSearchEvents.cancel();
        }
        this.eventOptions = [];
        this.eventLoading = false;
        return;
      }
      this.eventLoading = true;
      this.debouncedSearchEvents(text);
    },
    requestEvents(keyword) {
      const seq = ++this.searchEventSeq;
      this.$axios
        .post('/query_by_keyword', {
            pageNum: 1,
            pageSize: 20,
            name: keyword
        })
        .then((res) => {
          if (seq !== this.searchEventSeq) return;
          this.eventOptions = _.get(res, 'data.res.list') || [];
        })
        .catch((err) => {
          if (seq !== this.searchEventSeq) return;
          this.eventOptions = [];
          this.$message.error(err.response?.data?.message || '赛事搜索失败');
        })
        .finally(() => {
          if (seq === this.searchEventSeq) {
            this.eventLoading = false;
          }
        });
    },
    createCoupon() {
      if (!this.canCreateCoupon) {
        return this.$message.error('无创建优惠码权限');
      }
      if (!this.createForm.eventId) return this.$message.error('请选择适用赛事');
      if (!this.createForm.name || !this.createForm.name.trim()) {
        return this.$message.error('请填写优惠券名称');
      }
      if (!this.createForm.discountAmountYuan || this.createForm.discountAmountYuan <= 0) {
        return this.$message.error('请填写优惠金额');
      }
      if (this.createMode === 'custom') {
        return this.createCustom();
      }
      return this.batchCreate();
    },
    batchCreate() {
      this.creating = true;
      this.$axios
        .post('/coupon-code/batch-create', JSON.stringify({
          eventId: this.createForm.eventId,
          name: this.createForm.name.trim(),
          discountAmount: Math.round(this.createForm.discountAmountYuan * 100),
          count: this.createForm.count
        }), {
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          const list = _.get(res, 'data.res') || [];
          this.$message.success(`已生成 ${list.length} 个优惠码`);
          this.pageNum = 1;
          this.getData();
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '生成失败');
        })
        .finally(() => {
          this.creating = false;
        });
    },
    createCustom() {
      if (!this.createForm.code || !this.createForm.code.trim()) {
        return this.$message.error('请填写优惠码');
      }
      if (!this.createForm.usageLimit || this.createForm.usageLimit <= 0) {
        return this.$message.error('请填写总使用次数');
      }
      this.creating = true;
      this.$axios
        .post('/coupon-code/create-custom', JSON.stringify({
          eventId: this.createForm.eventId,
          name: this.createForm.name.trim(),
          code: this.createForm.code.trim(),
          discountAmount: Math.round(this.createForm.discountAmountYuan * 100),
          usageLimit: this.createForm.usageLimit
        }), {
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then(() => {
          this.$message.success('创建成功');
          this.pageNum = 1;
          this.getData();
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '创建失败');
        })
        .finally(() => {
          this.creating = false;
        });
    },
    getData() {
      this.$axios
        .get('/coupon-code/find_all', {
          params: {
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            eventId: this.filters.eventId,
            code: this.filters.code,
            status: this.filters.status
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.tableData = _.get(res, 'data.res.list') || [];
          this.total = _.get(res, 'data.res.total') || 0;
        });
    },
    handleSearch() {
      this.pageNum = 1;
      this.getData();
    },
    resetFilters() {
      this.filters = {
        eventId: '',
        code: '',
        status: ''
      };
      this.handleSearch();
    },
    onCurrentPageChange(page) {
      this.pageNum = page;
      this.getData();
    },
    changeStatus(row, action) {
      if (!this.canUpdateCoupon) {
        return this.$message.error('无启用/停用优惠码权限');
      }
      const text = action === 'disable' ? '停用' : '启用';
      this.$confirm(`确定${text}优惠码 ${row.code} 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios
          .post(`/coupon-code/${action}`, JSON.stringify({ id: row._id }), {
            headers: {
              'x-auth-token': localStorage.getItem('bde_token')
            }
          })
          .then(() => {
            this.$message.success(`${text}成功`);
            this.getData();
          })
          .catch((err) => {
            this.$message.error(err.response?.data?.message || `${text}失败`);
          });
      });
    },
    releaseLock(row) {
      if (!this.canUpdateCoupon) {
        return this.$message.error('无解除优惠码占用权限');
      }
      this.$confirm(`确定解除优惠码 ${row.code} 的待支付占用吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios
          .post('/coupon-code/release-lock', JSON.stringify({ id: row._id }), {
            headers: {
              'x-auth-token': localStorage.getItem('bde_token')
            }
          })
          .then(() => {
            this.$message.success('已解除占用');
            this.getData();
          })
          .catch((err) => {
            this.$message.error(err.response?.data?.message || '解除占用失败');
          });
      });
    },
    deleteCoupon(row) {
      if (!this.canDeleteCoupon) {
        return this.$message.error('无删除优惠码权限');
      }
      this.$confirm(`确定删除优惠码 ${row.code} 吗？删除后不可恢复。`, '提示', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios
          .post('/coupon-code/delete', JSON.stringify({ id: row._id }), {
            headers: {
              'x-auth-token': localStorage.getItem('bde_token')
            }
          })
          .then(() => {
            this.$message.success('删除成功');
            if (this.tableData.length === 1 && this.pageNum > 1) {
              this.pageNum -= 1;
            }
            this.getData();
          })
          .catch((err) => {
            this.$message.error(err.response?.data?.message || '删除失败');
          });
      });
    },
    openUsageDetails(row) {
      if (!this.canViewCoupon) {
        return this.$message.error('无查看优惠码使用详情权限');
      }
      this.usageDialog.visible = true;
      this.usageDialog.loading = true;
      this.usageDialog.coupon = row;
      this.usageDialog.list = [];
      this.$axios
        .get('/coupon-code/usage-details', {
          params: {
            id: row._id
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.usageDialog.coupon = _.get(res, 'data.res.coupon') || row;
          this.usageDialog.list = _.get(res, 'data.res.list') || [];
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '获取使用详情失败');
        })
        .finally(() => {
          this.usageDialog.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.create-form {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}
.event-select {
  width: 420px;
}
.coupon-name-input {
  width: 240px;
}
.coupon-code-input {
  width: 220px;
}
.filter-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.filter-input {
  width: 220px;
}
.status-select {
  width: 160px;
}
.empty-text {
  color: #909399;
}
.pager {
  margin-top: 20px;
}
.usage-summary {
  margin-bottom: 12px;
  color: #606266;
}
.coupon-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.coupon-actions .el-button {
  margin: 0;
}

@media (max-width: 768px) {
  .coupon-code-page {
    font-size: 14px;
  }

  .create-form {
    margin-bottom: 16px;
    padding-bottom: 16px;
  }

  .create-form >>> .el-form-item {
    display: block;
    margin-right: 0;
    margin-bottom: 14px;
  }

  .create-form >>> .el-radio-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 0;
    width: 100%;
  }

  .create-form >>> .el-radio-button {
    width: 50%;
  }

  .create-form >>> .el-radio-button__inner {
    width: 100%;
    padding-left: 8px;
    padding-right: 8px;
  }

  .create-form >>> .el-input-number {
    width: 100%;
  }

  .create-action-item,
  .create-action-item >>> .el-form-item__content,
  .create-action-item .el-button {
    width: 100%;
  }

  .filter-tools {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
  }

  .filter-tools .el-button {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    margin-left: 0;
  }

  .coupon-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .coupon-actions .el-button {
    width: 100%;
    min-width: 0;
    margin: 0;
    padding-left: 8px;
    padding-right: 8px;
  }
}
</style>
